/**
 * Free shipping progress bar — Something Splendid Co.
 * ---------------------------------------------------
 * Keeps every [data-ss-fsb] element in sync with the live cart total.
 *
 * Deliberately self-contained: it does NOT depend on Impulse internals,
 * because this theme is on Impulse 7.4.0 and a theme update would other-
 * wise break it. It watches the subtotal the customer can actually see and
 * mirrors it, so it stays correct no matter which script changed the cart
 * and never contradicts what is on screen.
 *
 * Initial state is rendered server-side in Liquid, so there is no flash
 * of an empty bar on load.
 */
(function () {
  'use strict';

  var SELF_FLAG = 'ss_fsb=1';

  var refreshTimer = null;
  var lastTotal = null;

  /* ---------------------------------------------------------------- *
   * Money formatting (mirrors Shopify's money filter)
   * ---------------------------------------------------------------- */
  function moneyFormat() {
    try {
      if (window.theme && window.theme.settings && window.theme.settings.moneyFormat) {
        return window.theme.settings.moneyFormat;
      }
    } catch (e) {}
    return '${{amount}}';
  }

  function withCommas(number, decimals, decimalSep, thousandsSep) {
    var n = (number / 100).toFixed(decimals);
    var parts = n.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1' + thousandsSep);
    return decimals && parts[1] ? whole + decimalSep + parts[1] : whole;
  }

  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Math.max(0, parseInt(cents, 10) || 0);

    var format = moneyFormat();
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    if (!match) return format;

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withCommas(cents, 0, '.', ','); break;
      case 'amount_with_comma_separator':
        value = withCommas(cents, 2, ',', '.'); break;
      case 'amount_no_decimals_with_comma_separator':
        value = withCommas(cents, 0, ',', '.'); break;
      case 'amount_with_space_separator':
        value = withCommas(cents, 2, ',', ' '); break;
      case 'amount_no_decimals_with_space_separator':
        value = withCommas(cents, 0, ',', ' '); break;
      default:
        value = withCommas(cents, 2, '.', ','); break;
    }
    return format.replace(placeholder, value);
  }

  /* ---------------------------------------------------------------- *
   * Rendering
   * ---------------------------------------------------------------- */
  function renderOne(el, totalCents) {
    var threshold = parseInt(el.getAttribute('data-threshold'), 10);
    if (!threshold || threshold <= 0) return;

    var remaining = Math.max(0, threshold - totalCents);
    var complete = remaining === 0;
    // Never paint a full bar unless free shipping is actually unlocked —
    // rounding would otherwise show 100% at $74.99.
    var percent = complete
      ? 100
      : Math.max(0, Math.min(99, Math.floor((totalCents / threshold) * 100)));
    var wasComplete = el.classList.contains('ss-fsb--complete');

    var message;
    if (complete) {
      message = el.getAttribute('data-text-success') || "You've unlocked free shipping!";
    } else {
      var template = el.getAttribute('data-text-progress') || "You're [amount] away from free shipping!";
      message = template.replace('[amount]', formatMoney(remaining));
    }

    var messageEl = el.querySelector('[data-ss-fsb-message]');
    if (messageEl && messageEl.textContent !== message) messageEl.textContent = message;

    var fillEl = el.querySelector('[data-ss-fsb-fill]');
    if (fillEl) fillEl.style.width = percent + '%';

    var trackEl = el.querySelector('[data-ss-fsb-track]');
    if (trackEl) {
      trackEl.setAttribute('aria-valuenow', String(percent));
      trackEl.setAttribute('aria-label', message);
    }

    el.classList.toggle('ss-fsb--complete', complete);

    // Celebrate only on the transition into "unlocked", never on every render.
    if (complete && !wasComplete) {
      el.classList.remove('ss-fsb--just-completed');
      void el.offsetWidth; // force reflow so the animation can replay
      el.classList.add('ss-fsb--just-completed');
    } else if (!complete) {
      el.classList.remove('ss-fsb--just-completed');
    }
  }

  function render(totalCents) {
    if (typeof totalCents !== 'number' || isNaN(totalCents)) return;
    lastTotal = totalCents;
    var bars = document.querySelectorAll('[data-ss-fsb]');
    for (var i = 0; i < bars.length; i++) renderOne(bars[i], totalCents);
  }

  /* ---------------------------------------------------------------- *
   * Reading the total
   *
   * The bar MUST agree with the subtotal the customer can see. Reading
   * /cart.js independently lets the two disagree — a page rendered at
   * $32 while the live cart is $64 produced "You're $11.00 away", which
   * is right about the cart and wrong about the screen. The screen wins.
   *
   * [data-subtotal] is rendered by Liquid and updated by the theme on
   * every cart change, in both the drawer and the cart page, so it is
   * the single source of truth. /cart.js is only a fallback for pages
   * that have no subtotal element at all.
   * ---------------------------------------------------------------- */
  function parseMoneyToCents(text) {
    if (!text) return null;
    var cleaned = String(text).replace(/[^\d.,]/g, '');
    if (!cleaned) return null;

    // Whichever separator comes last is the decimal point, but only if it
    // is followed by 1-2 digits ("1,234" is thousands, "98.00" is decimal).
    var dec = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
    var whole = cleaned, frac = '';
    if (dec > -1 && cleaned.length - dec - 1 <= 2) {
      whole = cleaned.slice(0, dec);
      frac = cleaned.slice(dec + 1);
    }
    whole = whole.replace(/[.,]/g, '');
    frac = (frac + '00').slice(0, 2);

    var cents = parseInt(whole, 10) * 100 + parseInt(frac, 10);
    return isNaN(cents) ? null : cents;
  }

  function subtotalNodes() {
    var raw = document.querySelectorAll('[data-cart-subtotal]');
    if (raw.length) return raw;
    return document.querySelectorAll('[data-subtotal]');
  }

  function readDisplayedTotal() {
    var raw = document.querySelector('[data-cart-subtotal]');
    if (raw) {
      var v = parseInt(raw.getAttribute('data-cart-subtotal'), 10);
      if (!isNaN(v)) return v;
    }
    var el = document.querySelector('[data-subtotal]');
    if (el) return parseMoneyToCents(el.textContent);
    return null;
  }

  // Fallback only: no subtotal anywhere on the page to read.
  function readCartFromNetwork() {
    if (!document.querySelector('[data-ss-fsb]')) return;

    var url = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart.js';
    url += (url.indexOf('?') === -1 ? '?' : '&') + SELF_FLAG;

    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (cart && typeof cart.total_price === 'number') render(cart.total_price);
      })
      .catch(function () { /* leave the server-rendered value in place */ });
  }

  function sync() {
    if (!document.querySelector('[data-ss-fsb]')) return;
    var cents = readDisplayedTotal();
    if (cents !== null) render(cents);
    else readCartFromNetwork();
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(sync, 60);
  }

  // Re-read the moment the theme rewrites the subtotal, whichever script did it.
  function watchSubtotals() {
    if (typeof MutationObserver !== 'function') return;
    var obs = new MutationObserver(scheduleRefresh);
    var nodes = subtotalNodes();
    for (var i = 0; i < nodes.length; i++) {
      obs.observe(nodes[i], { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['data-cart-subtotal'] });
    }
    // The drawer replaces its contents wholesale, so watch the container too.
    var drawer = document.getElementById('CartDrawer');
    if (drawer) obs.observe(drawer, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------- *
   * Change detection
   *
   * Earlier versions monkey-patched window.fetch and XMLHttpRequest to spot
   * cart mutations. That is a global side effect in a store running a dozen
   * third-party apps, and it became redundant once this watches the subtotal
   * directly, so it has been removed. A MutationObserver on [data-subtotal]
   * catches every change regardless of which script made it.
   * ---------------------------------------------------------------- */
  ['cart:updated', 'cart:build', 'cart:refresh', 'cart:open', 'ajaxProduct:added']
    .forEach(function (name) {
      document.addEventListener(name, scheduleRefresh);
    });

  // Returning to the tab: re-read what is on screen. Deliberately NOT a network
  // read - the cart may have changed in another tab, and showing that total next
  // to this page's stale subtotal is how the bar ended up claiming "$11.00 away"
  // on a $32.00 cart.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) scheduleRefresh();
  });

  /* ---------------------------------------------------------------- *
   * Boot
   * ---------------------------------------------------------------- */
  /**
   * Mount the bar on the cart page.
   *
   * sections/main-cart.liquid is ~1,800 lines of the original developers'
   * custom cart code, so we deliberately do not edit it. Instead we clone the
   * drawer's bar — which Liquid has already rendered with the correct total —
   * and drop it under the subtotal. No server-side edit, same result.
   */
  function mountOnCartPage() {
    var form = document.querySelector('#CartPageForm, form[data-location="page"]');
    if (!form) return;
    if (form.querySelector('[data-ss-fsb]')) return; // already rendered by Liquid

    var source = document.querySelector('#CartDrawer [data-ss-fsb]');
    if (!source) return;
    if (source.getAttribute('data-show-cart-page') === 'false') return;

    var anchor = form.querySelector('.cart__row--table');
    if (!anchor || !anchor.parentNode) return;

    var clone = source.cloneNode(true);
    clone.classList.remove('ss-fsb--drawer', 'ss-fsb--just-completed');
    clone.classList.add('ss-fsb--page');
    anchor.parentNode.insertBefore(clone, anchor.nextSibling);
  }

  function init() {
    mountOnCartPage();
    if (!document.querySelector('[data-ss-fsb]')) return;
    watchSubtotals();
    sync();
  }

  // init() is idempotent (mountOnCartPage guards against duplicates), so run it
  // both now and on DOMContentLoaded. This script is loaded with `defer`, but
  // running it twice is cheap insurance against ordering surprises.
  init();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  }

  // Expose for debugging / other scripts
  window.SSFreeShippingBar = {
    refresh: scheduleRefresh,
    sync: sync,
    readDisplayedTotal: readDisplayedTotal,
    parseMoneyToCents: parseMoneyToCents,
    render: render,
    formatMoney: formatMoney,
    mount: mountOnCartPage
  };
})();
